import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  Fab,
  useTheme,
  Avatar,
  Tooltip,
  styled
} from '@mui/material';
import {
  MaximizeCircle,
  MinusCirlce,
  CloseCircle,
  Call,
  CallSlash,
  Microphone2,
  MicrophoneSlash,
  VideoSlash,
  VolumeHigh,
  VolumeSlash,
  Notification,
  NotificationBing
} from 'iconsax-react';
import { usePersistentCall } from 'contexts/PersistentCallContext';
import VideoSessionContext from 'contexts/VideoSessionContext';
import useApi from 'hooks/useApi';
import useFirebase from 'hooks/useFirebase';
import { CallStatus } from 'types/auth';
import ringtoneManager from 'utils/ringtoneManager';

const callRingtoneUrl = "https://s3.eu-west-2.amazonaws.com/co.quikstep.dev/assets/ringtone-for-calling.mp3";

const OverlayCard = styled(Card)(({ theme }) => ({
  position: 'fixed',
  top: '20px',
  right: '20px',
  zIndex: 1400,
  minWidth: '320px',
  maxWidth: '400px',
  height: '65px',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.24)',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const IncomingCallCard = styled(Card)(({ theme }) => ({
  position: 'fixed',
  top: '20px',
  right: '20px',
  zIndex: 1400,
  minWidth: '280px',
  maxWidth: '350px',
  height: '65px',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.24)',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${theme.palette.success.main}`,
  animation: 'incoming-call-pulse 2s infinite',
}));

const MinimizedCard = styled(Card)(({ theme }) => ({
  position: 'fixed',
  top: '20px',
  right: '20px',
  zIndex: 1400,
  minWidth: '280px',
  maxWidth: '350px',
  width: 'auto',
  height: '65px',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 25px rgba(0, 0, 0, 0.2)',
  }
}));

const IncomingCallMinimizedCard = styled(Card)(({ theme }) => ({
  position: 'fixed',
  top: '20px',
  right: '20px',
  zIndex: 1400,
  minWidth: '280px',
  maxWidth: '350px',
  width: 'auto',
  height: '65px',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  //   border: `1px solid ${theme.palette.success.main}`,
  animation: 'incoming-call-pulse 2s infinite',
}));

const CallAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  fontSize: '14px',
  fontWeight: 'bold'
}));

const ActionButton = styled(Fab)(({ theme }) => ({
  width: 40,
  height: 40,
  minHeight: 40,
  margin: '0 4px',
  '&.MuiSpeedDial-fab': {
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  }
}));

const PersistentCallOverlay: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isCallActive, isCallMinimized, maximizeCall, minimizeCall, isOnCallPage, callData } = usePersistentCall();
  const videoSessionContext = useContext(VideoSessionContext);
  const { localAudioTrack, client, cleanupSession } = videoSessionContext || {};
  const { user, endSession, showToast } = useApi();
  const { updateCallStatus, addCallHistoryEntry, updateRecipients, getCallData, firebaseUid } = useFirebase();
  
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isSpeakerMuted, setIsSpeakerMuted] = useState(false);
  const [isRingtoneEnabled, setIsRingtoneEnabled] = useState(true);
  
  // NOTE: local audio refs removed — ringtone is handled by ringtoneManager

  // Use the session data from context
  const activeSession = callData;

  // Debug logging
  // (render-loop debug logging removed - it fired on every render)


  // Use centralized ringtone manager
  const stopRingtoneSoft = () => {
    try {
      ringtoneManager.stop();
      console.log('stopRingtoneSoft: delegated to ringtoneManager.stop()');
    } catch (e) {
      console.warn('stopRingtoneSoft error', e);
    }
  };


  // Initialize singleton ringtone manager once
  useEffect(() => {
    try {
      ringtoneManager.init(callRingtoneUrl);
    } catch (e) {
      console.warn('ringtoneManager.init failed', e);
    }

    // cleanup on unmount: ensure manager stopped
    return () => {
      try { ringtoneManager.stop(); } catch (e) {}
    };
  }, []);

  useEffect(() => {
    const isIncomingCall = activeSession?.status === CallStatus.RECEIVED ||
                          activeSession?.status === CallStatus.CALLING ||
                          activeSession?.status === CallStatus.RECEIVING;
    const shouldPlayRingtone = isCallActive && isIncomingCall && isRingtoneEnabled && !isSpeakerMuted;
  
    (async () => {
      try {
        if (shouldPlayRingtone) {
          // start via manager (manager handles idempotency)
          await ringtoneManager.play();
        } else {
          // stop via manager
          ringtoneManager.stop();
        }
      } catch (e) {
        console.warn('Ringtone play/stop decision error', e);
      }
    })();
  
    // cleanup: ensure stopped softly (manager stop is safe)
    return () => {
      try { ringtoneManager.stop(); } catch (e) {}
    };
  }, [isCallActive, activeSession?.status, isRingtoneEnabled, isSpeakerMuted]);
  
  // Stop ringtone when navigating to call page (call accepted) or call ends
  useEffect(() => {
    if (isOnCallPage || !isCallActive) {
      stopRingtoneSoft();
    }
  }, [isOnCallPage, isCallActive]);

  // Also stop on location changes immediately
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith('/session/')) {
      stopRingtoneSoft();
    }
  }, [location.pathname]);

  // Don't show overlay if not in an active call or if on the call page
  if (!isCallActive || isOnCallPage) return null;

  const getUserInitials = (name: string) => {
    if (!name?.trim()) return 'U';
    const words = name.trim().split(/\s+/).slice(0, 2);
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const handleMaximize = () => {
    const callId = callData?.transaction || callData?.id;
    if (callId) navigate(`/session/${callId}`);
    maximizeCall();
  };

  const handleMinimize = () => minimizeCall();

  // safe unpublish helper (local helper inside this component)
  const safeUnpublishAndLeave = async () => {
    const localClient = client as any;
    if (!localClient) return;

    try {
      // If localAudioTrack exists, unpublish only that
      if (localAudioTrack) {
        try {
          await localClient.unpublish([localAudioTrack]);
        } catch (e) {
          // ignore and try fallback below
          console.warn('safeUnpublishAndLeave: unpublish specific track failed', e);
        }
      } else {
        // Best-effort blanket unpublish if SDK supports it
        try {
          if (typeof localClient.unpublish === 'function') {
            // some SDK versions accept no args
            await localClient.unpublish();
          }
        } catch (e) {
          console.debug('safeUnpublishAndLeave: blanket unpublish failed or unsupported', e);
        }
      }
    } catch (e) {
      console.warn('safeUnpublishAndLeave: unexpected unpublish error', e);
    } finally {
      // always attempt to leave
      try {
        if (typeof localClient.leave === 'function') {
          await localClient.leave();
        }
      } catch (leaveErr) {
        console.warn('safeUnpublishAndLeave: leave failed', leaveErr);
      }
    }
  };

  const handleEndCall = async () => {
    if (!activeSession) return;

    try {
      stopRingtoneSoft();

      const callId = activeSession.transaction || activeSession.id;
      if (!callId) { showToast('Unable to end call - call ID not found', 'error'); return; }

      if (activeSession?.supportAccepted !== user.uid) {
        addCallHistoryEntry(callId, { status: CallStatus.SUPPORT_LEFT, datetime: new Date().toISOString(), user: user!.uid });

        if (cleanupSession) {
          cleanupSession();
        } else {
          // Fallback cleanup: use safe unpublish/leave helper
          try {
            if (localAudioTrack) { localAudioTrack.stop(); localAudioTrack.close(); }
          } catch (e) { console.warn('localAudioTrack stop/close failed', e); }

          await safeUnpublishAndLeave();
        }

        showToast('Exiting the session. Thank you!', 'success');
        navigate('/');
        return;
      }

      const response = await endSession(callId);
      const { success, message } = response;

      if (success) {
        addCallHistoryEntry(callId, { status: CallStatus.SUPPORT_LEFT, datetime: new Date().toISOString(), user: user!.uid });
        if (cleanupSession) {
          cleanupSession();
        } else {
          try {
            if (localAudioTrack) { localAudioTrack.stop(); localAudioTrack.close(); }
          } catch (e) { console.warn('localAudioTrack stop/close failed', e); }

          await safeUnpublishAndLeave();
        }

        addCallHistoryEntry(callId, { status: CallStatus.COMPLETED, datetime: new Date().toISOString(), user: user!.uid });
        updateCallStatus(callId, CallStatus.COMPLETED);
        showToast('Session has been ended', 'success');
        navigate('/');
      } else {
        showToast(message, 'error');
      }
    } catch (error: any) {
      showToast(error.message, 'error');
    }
  };

  const toggleMute = () => {
    if (!localAudioTrack) { showToast('Audio track not available', 'warning'); return; }
    try {
      const newMutedState = !isAudioMuted;
      const success = newMutedState ? localAudioTrack.setEnabled(false) : localAudioTrack.setEnabled(true);
      if (success !== undefined && success === false) { showToast('Failed to toggle microphone', 'error'); return; }
      setIsAudioMuted(newMutedState);
      if (activeSession?.transaction) addCallHistoryEntry(activeSession.transaction, { status: newMutedState ? CallStatus.SUPPORT_MUTE : CallStatus.SUPPORT_UNMUTE, datetime: new Date().toISOString(), user: user!.uid });
    } catch (e) { showToast('Failed to toggle microphone', 'error'); }
  };

  const toggleSpeaker = () => {
    const newMutedState = !isSpeakerMuted;
    setIsSpeakerMuted(newMutedState);

    const audioElements = document.querySelectorAll('video, audio');
    audioElements.forEach((element: any) => { if (element.volume !== undefined) element.muted = newMutedState; });

    if (newMutedState) stopRingtoneSoft();
  };

  const toggleRingtone = () => {
    const newEnabledState = !isRingtoneEnabled;
    setIsRingtoneEnabled(newEnabledState);
    if (!newEnabledState) stopRingtoneSoft();
    showToast(`Ringtone ${newEnabledState ? 'enabled' : 'disabled'}`, 'info');
  };

  const formatCallDuration = () => {
    if (!activeSession?.initiated) return '00:00';
    const now = Date.now();
    const duration = Math.floor((now - activeSession.initiated) / 1000);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Decline an incoming call. Previously the only ways out of a ringing call were
  // to answer it or silence the ringtone, so a call you could not take kept
  // ringing and the caller was left waiting for the full timeout.
  //
  // Declining removes only US from `recipients` — that list is exactly what
  // useCallQueue/useUserCalls ring on, so any other agent the call was offered to
  // keeps ringing. If we were the last one, nobody can take it, so the call is
  // genuinely missed and we say so: that is what stops the app ringing and shows
  // it "Call Ended" instead of waiting out its own timer.
  const handleDeclineCall = async () => {
    if (!activeSession) return;

    stopRingtoneSoft();

    const callId = activeSession.transaction || activeSession.id;
    if (!callId) { showToast('Unable to decline call - call ID not found', 'error'); return; }

    const myUid = firebaseUid || user?.uid;

    try {
      addCallHistoryEntry(callId, {
        status: CallStatus.SUPPORT_LEFT,
        datetime: new Date().toISOString(),
        user: user?.uid
      });

      // Read the live list rather than the snapshot this card was rendered from —
      // another agent may have declined in between and we would write them back in.
      const current = await getCallData(callId);
      const remaining = ((current?.recipients || activeSession.recipients || []) as string[])
        .filter((uid: string) => uid !== myUid);

      await updateRecipients(callId, remaining);

      if (remaining.length === 0) {
        await updateCallStatus(callId, CallStatus.MISSED_CALL);
      }

      showToast('Call declined', 'success');
    } catch (error: any) {
      console.error('Error declining call:', error);
      showToast('Failed to decline call', 'error');
    }
  };

  const handleAcceptCall = async () => {
    if (!activeSession) return;

    // immediate soft stop BEFORE navigation / async work
    stopRingtoneSoft();

    try {
      const callId = activeSession.transaction || activeSession.id;
      if (!callId) { showToast('Unable to accept call - call ID not found', 'error'); return; }

      if (videoSessionContext?.setCallUid) videoSessionContext.setCallUid(callId);
      navigate(`/session/${callId}`);
      maximizeCall();
    } catch (error: any) {
      console.error('Error accepting call:', error);
      showToast('Failed to accept call', 'error');
    }
  };

  // Render minimized / expanded UI (unchanged visually)
  if (isCallMinimized) {
    const isIncomingCall = activeSession?.status === CallStatus.RECEIVED || 
                          activeSession?.status === CallStatus.CALLING ||
                          activeSession?.status === CallStatus.RECEIVING;
    const CardComponent = isIncomingCall ? IncomingCallMinimizedCard : MinimizedCard;
    return (
      <CardComponent onClick={!isIncomingCall ? handleMaximize : undefined}>
        <CardContent sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2, '&:last-child': { pb: 1.5 } }}>
          <CallAvatar>{getUserInitials(activeSession?.fromName || 'Unknown')}</CallAvatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight="bold" noWrap>{activeSession?.fromName || 'Unknown Caller'}</Typography>
            <Typography variant="caption" color={isIncomingCall ? 'success.main' : 'text.secondary'} sx={{ fontWeight: isIncomingCall ? 'bold' : 'normal' }}>
              {isIncomingCall ? (activeSession?.status === CallStatus.CALLING ? (activeSession?.sessionType === 'CHAT' ? 'Incoming chat...' : 'Incoming call...') : activeSession?.status === CallStatus.RECEIVING ? 'Connecting...' : 'Ringing...') : `${activeSession?.sessionType === 'CHAT' ? 'Chat' : 'Call'} in progress • ${formatCallDuration()}`}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {isIncomingCall ? (
              <>
                <Tooltip title={isRingtoneEnabled ? 'Disable Ringtone' : 'Enable Ringtone'}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleRingtone(); }} color={isRingtoneEnabled ? 'default' : 'warning'}>
                    {isRingtoneEnabled ? <NotificationBing size={14} /> : <Notification size={14} />}
                  </IconButton>
                </Tooltip>

                <Tooltip title={activeSession?.sessionType === 'CHAT' ? 'Decline Chat' : 'Decline Call'}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeclineCall(); }} sx={{ color: 'error.main', backgroundColor: 'error.lighter', '&:hover': { backgroundColor: 'error.light' } }}>
                    <CallSlash size={18} />
                  </IconButton>
                </Tooltip>

                <Tooltip title={activeSession?.sessionType === 'CHAT' ? 'Answer Chat' : 'Answer Call'}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); stopRingtoneSoft(); handleAcceptCall(); }} sx={{ color: 'success.main', backgroundColor: 'success.lighter', '&:hover': { backgroundColor: 'success.light' } }}>
                    <Call size={18} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                {(activeSession?.userMuted || activeSession?.userHidden) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    {activeSession?.userMuted && (<Tooltip title="User muted"><MicrophoneSlash size={14} color={theme.palette.error.main} /></Tooltip>)}
                    {activeSession?.userHidden && (<Tooltip title="User camera off"><VideoSlash size={14} color={theme.palette.error.main} /></Tooltip>)}
                  </Box>
                )}

                <Tooltip title={activeSession?.sessionType === 'CHAT' ? 'Open Chat' : 'Open Call'}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMaximize(); }} color="primary"><Call size={18} /></IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </CardContent>
      </CardComponent>
    );
  }

  // Expanded view - unchanged layout
  const isIncomingCall = activeSession?.status === CallStatus.RECEIVED || 
                        activeSession?.status === CallStatus.CALLING ||
                        activeSession?.status === CallStatus.RECEIVING;
  const CardComponent = isIncomingCall ? IncomingCallCard : OverlayCard;

  return (
    <CardComponent>
      <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: isIncomingCall ? theme.palette.success.lighter : 'inherit' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CallAvatar>{getUserInitials(activeSession?.fromName || 'Unknown')}</CallAvatar>
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">{activeSession?.fromName || 'Unknown Caller'}</Typography>
            <Typography variant="caption" color={isIncomingCall ? 'success.main' : 'text.secondary'} sx={{ fontWeight: isIncomingCall ? 'bold' : 'normal' }}>{isIncomingCall ? (activeSession?.status === CallStatus.CALLING ? (activeSession?.sessionType === 'CHAT' ? 'Incoming Chat' : 'Incoming Call') : activeSession?.status === CallStatus.RECEIVING ? 'Connecting...' : 'Ringing...') : `${activeSession?.key || (activeSession?.sessionType === 'CHAT' ? 'Chat in progress' : 'Call in progress')} • ${formatCallDuration()}`}</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {activeSession?.sessionType !== 'CHAT' && (<Tooltip title="Minimize"><IconButton size="small" onClick={handleMinimize}><MinusCirlce size={16} /></IconButton></Tooltip>)}

          {activeSession?.sessionType === 'CHAT' ? (
            <Tooltip title="End Chat"><IconButton size="small" onClick={handleEndCall} sx={{ color: 'error.main', backgroundColor: 'error.lighter', '&:hover': { backgroundColor: 'error.light' } }}><CallSlash size={16} /></IconButton></Tooltip>
          ) : (
            <Tooltip title="End Call"><IconButton size="small" onClick={handleEndCall} color="error"><CloseCircle size={16} /></IconButton></Tooltip>
          )}
        </Box>
      </Box>

      <CardContent sx={{ p: 2, pt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: isIncomingCall ? theme.palette.success.main : theme.palette.success.main, animation: isIncomingCall ? 'incoming-call-pulse 2s infinite' : 'pulse 2s infinite' }} />
          <Typography variant="body2" color={isIncomingCall ? 'success.main' : 'success.main'} fontWeight="medium">{isIncomingCall ? (activeSession?.status === CallStatus.CALLING ? 'Ringing...' : activeSession?.status === CallStatus.RECEIVING ? 'Connecting...' : 'Incoming...') : (activeSession?.status === CallStatus.IN_PROGRESS ? 'Connected' : 'Connecting...')}</Typography>
        </Box>

        {isIncomingCall && (
          <Box sx={{ p: 2, mb: 2, borderRadius: 2, backgroundColor: theme.palette.success.lighter, textAlign: 'center' }}>
            <Typography variant="body2" color="success.main" fontWeight="bold">{activeSession?.sessionType === 'CHAT' ? '💬 Incoming Chat' : '📞 Incoming Call'}</Typography>
            <Typography variant="caption" color="text.secondary">{activeSession?.sessionType === 'CHAT' ? 'Tap "Answer" to join the chat' : 'Tap "Answer" to join the call'}</Typography>
          </Box>
        )}

        {!isIncomingCall && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {activeSession?.userMuted && (<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, backgroundColor: theme.palette.error.lighter }}><MicrophoneSlash size={14} color={theme.palette.error.main} /><Typography variant="caption" color="error.main">User muted</Typography></Box>)}
            {activeSession?.userHidden && (<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, backgroundColor: theme.palette.warning.lighter }}><VideoSlash size={14} color={theme.palette.warning.main} /><Typography variant="caption" color="warning.main">Camera off</Typography></Box>)}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isIncomingCall ? (
              <>
                <Tooltip title={isRingtoneEnabled ? 'Disable Ringtone' : 'Enable Ringtone'}>
                  <ActionButton size="small" color={isRingtoneEnabled ? 'default' : 'warning'} onClick={toggleRingtone}>{isRingtoneEnabled ? <NotificationBing size={18} /> : <Notification size={18} color="#fff" />}</ActionButton>
                </Tooltip>

                <Tooltip title={isSpeakerMuted ? 'Unmute Speaker' : 'Mute Speaker'}>
                  <ActionButton size="small" color={isSpeakerMuted ? 'error' : 'default'} onClick={toggleSpeaker}>{isSpeakerMuted ? <VolumeSlash size={20} color="#fff" /> : <VolumeHigh size={20} />}</ActionButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title={isAudioMuted ? 'Unmute' : 'Mute'}>
                <ActionButton size="small" color={isAudioMuted ? 'error' : 'default'} onClick={toggleMute}>{isAudioMuted ? <MicrophoneSlash size={20} color="#fff" /> : <Microphone2 size={20} />}</ActionButton>
              </Tooltip>
            )}

            <Tooltip title={isIncomingCall
              ? (activeSession?.sessionType === 'CHAT' ? 'Decline Chat' : 'Decline Call')
              : (activeSession?.sessionType === 'CHAT' ? 'End Chat' : 'End Call')}>
              <ActionButton size="small" color="error" onClick={isIncomingCall ? handleDeclineCall : handleEndCall}><CallSlash size={20} color="#fff" /></ActionButton>
            </Tooltip>
          </Box>

          <Tooltip title={isIncomingCall ? (activeSession?.status === CallStatus.RECEIVING ? "Joining..." : (activeSession?.sessionType === 'CHAT' ? "Answer Chat" : "Answer Call")) : (activeSession?.sessionType === 'CHAT' ? "Open full chat interface" : "Open full call interface") }>
            <ActionButton size="small" color={isIncomingCall ? "success" : "primary"} onClick={isIncomingCall ? handleAcceptCall : handleMaximize} sx={isIncomingCall ? { backgroundColor: 'success.main', '&:hover': { backgroundColor: 'success.dark' } } : {}}>
              {isIncomingCall ? <Call size={20} color="#fff" /> : <MaximizeCircle size={20} color="#fff" />}
            </ActionButton>
          </Tooltip>
        </Box>
      </CardContent>

      <style>{`@keyframes pulse {0% { opacity: 1; }50% { opacity: 0.5; }100% { opacity: 1; }}@keyframes incoming-call-pulse {0% { transform: scale(1); box-shadow: 0 6px 30px rgba(76, 175, 80, 0.4); }50% { transform: scale(1.02); box-shadow: 0 8px 40px rgba(76, 175, 80, 0.6); }100% { transform: scale(1); box-shadow: 0 6px 30px rgba(76, 175, 80, 0.4); }}`}</style>
    </CardComponent>
  );
};

export default PersistentCallOverlay;
